'use client';

import type { SignupScreeningQuestion } from '@galapagos/shared/registration';
import type { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupQuizContent } from '../hooks/useSignupQuizContent';
import { useLeaveScreening } from '../screening/useLeaveScreening';
import { screeningQuestions, type WordedQuizQuestion } from '../services/quiz-content';
import { SignupNotImplementedError } from '../services/signup-api/signup-api';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { QuizQuestionScreen } from './QuizQuestionScreen';
import { QuizStateScreen } from './QuizStepScreens';
import { EarlierAnswersGate } from './EarlierAnswersGate';

/**
 * The served question with its words from the signup catalog, which keys them
 * by answer name and option value. The past-3-months line names the step only
 * when the chain has more than one question.
 */
function withCatalogWords(
  t: TFunction,
  question: SignupScreeningQuestion,
  position: number,
  total: number,
): { question: WordedQuizQuestion; context: string } {
  const answerName = question.answerName;
  return {
    question: {
      answerName,
      saveAs: question.saveAs,
      title: t(`quiz.taps2.questions.${answerName}.title`),
      description:
        total > 1
          ? t('quiz.taps2.periodStep', { step: position + 1, total })
          : t('quiz.taps2.period'),
      options: question.options.map((option) => ({
        value: option.value,
        label: t(`quiz.taps2.questions.${answerName}.options.${option.value}`),
      })),
    },
    context: t(`quiz.taps2.questions.${answerName}.context`),
  };
}

/**
 * The member's chain, one question at a time, on one page. Which question is
 * showing is held here rather than in the address, so a two-question chain
 * leaves no trace in page views that a one-question chain does not.
 */
function ScreeningStep() {
  // A reload on this step refills the flow's params from its URL.
  useLegacySignupParams();
  const content = useSignupQuizContent();
  const selection = useSignupFlowStore((state) => state.programSelection);
  const leave = useLeaveScreening();
  const { t } = useTranslation('signup');
  // Leave at most once, even if the effect runs again before the route changes.
  const leftRef = useRef(false);
  const [position, setPosition] = useState(0);

  const questions = content.data ? screeningQuestions(content.data, selection ?? []) : null;
  const question = questions?.[position];
  const nothingToAsk = questions !== null && question === undefined;

  useEffect(() => {
    if (!nothingToAsk || leftRef.current) return;
    leftRef.current = true;
    leave();
  }, [nothingToAsk, leave]);

  if (content.isPending || (content.isError && content.isFetching)) {
    return <QuizStateScreen title={t('quiz.loading')} busy />;
  }
  if (content.isError) {
    // No backend serves the content yet on deployed builds: stand in for the
    // segment, so the path still clicks through.
    if (content.error instanceof SignupNotImplementedError) {
      return (
        <QuizStateScreen
          title={t('placeholder.title')}
          description={t('placeholder.description')}
          action={{ kind: 'continue', onPress: leave }}
        />
      );
    }
    return (
      <QuizStateScreen
        title={t('quiz.loadErrorTitle')}
        error={t('quiz.loadError')}
        action={{ kind: 'retry', onPress: () => void content.refetch() }}
      />
    );
  }
  if (!question || !questions) return null;

  const hasNext = position + 1 < questions.length;
  const worded = withCatalogWords(t, question, position, questions.length);
  return (
    <QuizQuestionScreen
      // A fresh mount per question moves focus to its heading and clears the
      // previous answer, as moving to a new page did.
      key={position}
      question={worded.question}
      context={worded.context}
      documentTitle={t('quiz.screeningTitle')}
      onAnswered={() => (hasNext ? setPosition(position + 1) : leave())}
    />
  );
}

/** `/screening/`: the TAPS 2 questions about the member's main program (v9 1.13, 1.13b). */
export function ScreeningScreen() {
  return (
    <EarlierAnswersGate step="screening">
      <ScreeningStep />
    </EarlierAnswersGate>
  );
}
